<script lang="ts" setup>
  import { UIFormItem, UIButton, UIInputGroup, UIInput, UITooltip, UIContentWrap } from '@gohighlevel/ghl-ui'
  import { Tag01Icon } from '@gohighlevel/ghl-icons/24/outline'
  import { FloatingCustomSelect } from '../CustomSelectTrigger'
  import { useCustomValues } from '../../composition'
  import type { CustomObjectLabels } from '../../types'
  import { PropType, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  const props = defineProps({
    id: { type: String, required: true },
    label: { type: String, required: true },
    value: { type: String, required: true },
    disabled: { type: Boolean, default: false },
    withCustomValueBtn: { type: Boolean, default: true },
    showFeedback: { type: Boolean, default: true },
    // When true, shows the required (*) mark next to the label.
    required: { type: Boolean, default: false },
    // Placeholder text for the input.
    placeholder: { type: String, default: '' },
    // When set, the field renders in an error state with this message shown below it.
    error: { type: String, default: '' },
    customObjectLabels: {
      type: Object as PropType<CustomObjectLabels>,
      default: () => ({}),
    },
  })
  const emits = defineEmits(['update:value'])
  const i18nLocale = useI18n()
  const { t } = i18nLocale
  const { customMenuOptionsWithoutOpp } = useCustomValues('customValues', () => props.customObjectLabels)

  const onChange = (val: string) => {
    emits('update:value', val)
  }
  const onSelect = (val: string) => {
    emits('update:value', (props.value || '') + val)
  }

  const show = ref(false)
  const showCustomValues = (e: MouseEvent) => {
    e.stopPropagation()
    show.value = !show.value
  }
  const hideCustomValues = () => {
    show.value = false
  }
</script>

<template>
  <div class="w-full" :key="id">
    <UIContentWrap :locale="i18nLocale?.locale?.value || 'en-US'" class="p-0" style="display: contents">
      <UIFormItem
        :label="props.label"
        :required="props.required"
        :show-feedback="props.showFeedback || !!props.error"
        :validation-status="props.error ? 'error' : undefined"
        :feedback="props.error || undefined"
      >
        <UIInputGroup>
          <UIInput
            :id="'input-field-' + props.id"
            :model-value="value"
            @update:model-value="onChange"
            :disabled="disabled"
            :placeholder="props.placeholder"
          />
          <FloatingCustomSelect
            v-if="props.withCustomValueBtn"
            :custom-menus="customMenuOptionsWithoutOpp"
            @on-select="onSelect"
            :show="show"
            @onClickOutSide="hideCustomValues"
          >
            <template #trigger>
              <UIButton
                :disabled="disabled"
                :id="'custom-values-trigger-' + id"
                size="medium"
                class="custom-menu__button mx-1"
                :text="false"
                :aria-label="t('common.addCustomValues')"
                @click="showCustomValues"
              >
                <UITooltip>
                  <template #trigger>
                    <Tag01Icon class="h-4 w-4" />
                  </template>
                  {{ $t('common.customValues') }}
                </UITooltip>
              </UIButton>
            </template>
          </FloatingCustomSelect>
        </UIInputGroup>
      </UIFormItem>
    </UIContentWrap>
  </div>
</template>
